import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import api from '../../utils/api';
import { loadDocumentPreview, releasePreview } from '../../utils/documentPreview';
import { formatPackWhen, packOutcomeLabel, packReceiptLabel } from '../../utils/dataPackLog';

function Fact({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">{value || '—'}</p>
    </div>
  );
}

export default function BuildingDataPackLog() {
  const { id: buildingId, logId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [memo, setMemo] = useState('');
  const [to, setTo] = useState('');
  const [memoReady, setMemoReady] = useState(false);

  const backTo = `/buildings/${buildingId}/paperwork?tab=council`;

  const { data: log, isLoading, isError, error } = useQuery({
    queryKey: ['data-pack-log', buildingId, logId],
    queryFn: async () =>
      (await api.get(`/buildings/${buildingId}/data-pack/logs/${logId}`)).data?.data,
    enabled: !!buildingId && !!logId,
  });

  useEffect(() => {
    if (!log || memoReady) return;
    setMemo(log.memo || '');
    setTo(log.recipient_email || '');
    setMemoReady(true);
  }, [log, memoReady]);

  const sendMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/buildings/${buildingId}/data-pack/logs/${logId}/send`, {
          to: to.trim(),
          memo: memo.trim(),
        })
      ).data?.data,
    onSuccess: (data) => {
      toast.success(data?.mailed ? 'Document sent' : 'Email not delivered (SMTP stub)');
      queryClient.invalidateQueries({ queryKey: ['data-pack-log', buildingId, logId] });
      queryClient.invalidateQueries({ queryKey: ['data-pack-logs', buildingId] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Send failed'),
  });

  const printDocument = async () => {
    if (!log?.document_id) {
      toast.error('No document file on this pack');
      return;
    }
    let preview;
    try {
      preview = await loadDocumentPreview(
        api,
        `/buildings/${buildingId}/documents`,
        log.document_id,
        { name: log.file_name || 'building-data-pack.pdf', mimeType: 'application/pdf' }
      );
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Cannot open document');
      return;
    }
    const w = window.open('', '_blank');
    if (!w) {
      releasePreview(preview);
      toast.error('Popup blocked — allow popups to print');
      return;
    }
    if (preview.kind === 'html' && preview.html) {
      w.document.write(preview.html);
      w.document.close();
      w.focus();
      w.print();
      return;
    }
    if (preview.blobUrl) {
      w.location.href = preview.blobUrl;
      w.focus();
      setTimeout(() => {
        try {
          w.print();
        } catch (_) {
          /* user can print from the new tab */
        }
      }, 400);
      return;
    }
    releasePreview(preview);
    toast.error('Print not available for this file');
  };

  const who = [log?.actor_name, log?.actor_id].filter(Boolean).join(' · ');

  return (
    <div className="pb-28">
      <BuildingPageHeading
        buildingId={buildingId}
        title="Work log"
        extraTrail={[
          { label: 'Paperwork', to: `/buildings/${buildingId}/paperwork?tab=council` },
          { label: 'Data pack' },
        ]}
      />

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && (
        <p className="text-sm text-red-600">{error?.response?.data?.error || 'Pack log not found'}</p>
      )}

      {log && (
        <div className="max-w-2xl space-y-4">
          <div className="rounded-xl border bg-white p-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {packOutcomeLabel(log.outcome)}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Fact label="When" value={formatPackWhen(log.created_at)} />
              <Fact label="To" value={log.recipient_email} />
              <Fact label="What" value={log.what_text || 'Building data pack'} />
              <Fact label="Why" value={log.why_text} />
              <Fact label="Who" value={who} />
              <Fact label="Received and opened" value={packReceiptLabel(log)} />
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4 space-y-3">
            <label className="block text-sm font-medium text-gray-800">
              Memo (required to confirm)
              <textarea
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm min-h-[88px]"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="What you did — call, chase, lodge, etc."
              />
            </label>
            <div>
              <p className="text-sm font-medium text-gray-800">Saved or sent document file</p>
              <p className="mt-1 text-sm text-gray-900">{log.file_name || '—'}</p>
            </div>
            <label className="block text-sm font-medium text-gray-800">
              Send to
              <input
                type="email"
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="name@example.com"
              />
            </label>
          </div>
        </div>
      )}

      <div className="fixed bottom-0 inset-x-0 z-40 border-t bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
        <div className="max-w-2xl mx-auto flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="px-4 py-2.5 rounded-lg border bg-white text-sm font-semibold text-gray-800"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="px-4 py-2.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-950 text-sm font-semibold hover:bg-amber-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!log?.document_id}
            onClick={printDocument}
            className="px-4 py-2.5 rounded-lg border bg-white text-sm font-semibold text-gray-800 disabled:opacity-40"
          >
            Print
          </button>
          <button
            type="button"
            disabled={sendMutation.isPending || !memo.trim() || !to.trim() || !log?.document_id}
            onClick={() => sendMutation.mutate()}
            className="px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-40"
          >
            {sendMutation.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
